import { emit } from "@zapier/events-toolkit-browser";
import { WebAnalyticsTrackingUserInteractionUserInteractionEvent } from "@zapier/avro-events";

import {
  DestinationUrlAddressEnum,
  EventAction,
  InteractionName,
  InteractionRawTextEnum,
  OptionSelected,
  RevenueSystemName,
  SectionName,
  UserInteractionEvent,
} from "./trackingTypes";
import { PlanType } from "@/types";

// AVRO schema: https://gitlab.com/zapier/avro-schemas/-/blob/main/schemas/web_analytics/tracking/user_interaction/user_interaction_event.avsc
export const emitUserInteractionEvent = (
  eventDetails: Partial<WebAnalyticsTrackingUserInteractionUserInteractionEvent.EventFields>
) => {
  const eventData = {
    ...eventDetails,
    system_name: RevenueSystemName,
    option_selected: eventDetails.option_selected ?? "",
    event_action: eventDetails.event_action,
  };

  return emit(eventData, UserInteractionEvent);
};

export const emitContactSalesClickEvent = ({
  planType,
}: {
  planType: PlanType;
}) => {
  return emitUserInteractionEvent({
    destination_url_address: DestinationUrlAddressEnum.contact_sales,
    event_action: EventAction.click,
    interaction_name: InteractionName.call_to_action,
    interaction_raw_text: InteractionRawTextEnum.contact_sales,
    option_selected: OptionSelected.cta_button,
    custom_tracking_fields: {
      plan_type: planType,
    },
  });
};

export const emitTryFreePlanClickEvent = ({
  planType,
}: {
  planType: PlanType;
}) => {
  return emitUserInteractionEvent({
    destination_url_address: DestinationUrlAddressEnum.sign_up,
    event_action: EventAction.click,
    interaction_name: InteractionName.call_to_action,
    interaction_raw_text: InteractionRawTextEnum.try_free,
    option_selected: OptionSelected.cta_button,
    custom_tracking_fields: {
      plan_type: planType,
    },
  });
};

export const emitOpenFAQClickEvent = ({
  interactionRawText,
}: {
  interactionRawText: string;
}) => {
  return emitUserInteractionEvent({
    event_action: EventAction.click,
    interaction_name: InteractionName.toggle,
    interaction_raw_text: interactionRawText,
    option_selected: OptionSelected.opening,
    custom_tracking_fields: {
      section_name: SectionName.FAQs,
    },
  });
};

export const emitFeatureClickEvent = ({
  interactionRawText,
  destinationUrlAddress,
}: {
  destinationUrlAddress: string | null;
  interactionRawText: string;
}) => {
  return emitUserInteractionEvent({
    destination_url_address: destinationUrlAddress,
    event_action: EventAction.click,
    interaction_name: InteractionName.navigation,
    interaction_raw_text: interactionRawText,
    option_selected: OptionSelected.cta_button,
  });
};

export const emitTaskTierSelect = ({
  planType,
  taskTier,
}: {
  planType: string;
  taskTier: number;
}) => {
  return emitUserInteractionEvent({
    event_action: EventAction.select,
    interaction_name: InteractionName.task_tier_option,
    custom_tracking_fields: {
      plan_type: planType,
      task_tier: taskTier.toString(),
    },
  });
};

export const emitSectionInViewEvent = ({
  sectionName,
}: {
  sectionName: string;
}) => {
  return emitUserInteractionEvent({
    event_action: EventAction.in_view,
    interaction_name: InteractionName.component_scrolled_into_view,
    option_selected: OptionSelected.section,
    custom_tracking_fields: {
      section_name: sectionName,
    },
  });
};
